import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Modal, Form, InputNumber, Radio, Tag, message } from 'antd';
import request from '../utils/request';
import { fmtTime } from '../utils/format';

// 积分账户：全部用户的积分余额列表，支持搜索与人工调分（发放/扣减）
const PointsAccounts: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [adjustUser, setAdjustUser] = useState<any>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchList = async (p = page, kw = keyword) => {
    setLoading(true);
    try {
      const res: any = await request.get('/points', {
        params: { page: p, pageSize: 10, keyword: kw || undefined },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  const fetchRecords = async (userId: number) => {
    setRecordsLoading(true);
    try {
      const res: any = await request.get(`/points/records/${userId}`);
      setRecords(res || []);
    } finally {
      setRecordsLoading(false);
    }
  };

  const openAdjust = (record: any) => {
    form.resetFields();
    form.setFieldsValue({ type: 'add', amount: undefined, reason: '' });
    setAdjustUser(record);
    setModalOpen(true);
    fetchRecords(record.userId);
  };

  const handleAdjust = async () => {
    const values = await form.validateFields();
    const delta = values.type === 'add' ? values.amount : -values.amount;
    setSubmitting(true);
    try {
      await request.post('/points/adjust', {
        userId: adjustUser.userId,
        delta,
        reason: `后台调分：${values.reason.trim()}`,
      });
      message.success('调分成功');
      setModalOpen(false);
      setAdjustUser(null);
      fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: '用户', width: 80, render: (_: any, r: any) => `#${r.userId}` },
    {
      title: '昵称/姓名', width: 150,
      render: (_: any, r: any) => r.user?.realName || r.user?.nickname || '-',
    },
    { title: '手机号', width: 130, render: (_: any, r: any) => r.user?.phone || '-' },
    {
      title: '当前积分', dataIndex: 'total', width: 100,
      render: (v: number) => <Tag color="gold" style={{ fontSize: 13 }}>{v}</Tag>,
    },
    {
      title: '最近变动', dataIndex: 'updatedAt', width: 160,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '操作', width: 110, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Button size="small" type="primary" onClick={() => openAdjust(r)}>调整积分</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>积分账户</h2>
        <Input.Search
          allowClear
          placeholder="搜索昵称/姓名/手机号"
          style={{ width: 240 }}
          onSearch={(v) => { setKeyword(v); setPage(1); fetchList(1, v); }}
        />
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Modal
        title={`调整积分 - ${adjustUser?.user?.realName || adjustUser?.user?.nickname || `用户#${adjustUser?.userId}`}`}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setAdjustUser(null); }}
        onOk={handleAdjust}
        confirmLoading={submitting}
        okText="确认调整"
        width={560}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          当前积分：<Tag color="gold" style={{ fontSize: 14 }}>{adjustUser?.total ?? 0}</Tag>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="调整方向" initialValue="add">
            <Radio.Group
              options={[
                { label: '发放（增加）', value: 'add' },
                { label: '扣减（减少）', value: 'sub' },
              ]}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
          <Form.Item
            name="amount" label="调整数量"
            rules={[{ required: true, message: '请输入调整数量' }]}
          >
            <InputNumber min={1} precision={0} placeholder="如 100" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason" label="调分原因"
            rules={[{ required: true, message: '请填写调分原因' }]}
          >
            <Input.TextArea rows={2} placeholder="如：活动奖励 / 补偿 / 数据纠错" maxLength={100} />
          </Form.Item>
        </Form>

        <div style={{ fontWeight: 600, margin: '8px 0' }}>最近积分流水</div>
        <Table
          size="small"
          loading={recordsLoading}
          dataSource={records}
          rowKey="id"
          pagination={false}
          scroll={{ y: 220 }}
          columns={[
            {
              title: '变动', dataIndex: 'delta', width: 70,
              render: (v: number) => (
                <span style={{ color: v > 0 ? '#065F46' : '#B45309', fontWeight: 600 }}>
                  {v > 0 ? '+' : ''}{v}
                </span>
              ),
            },
            { title: '原因', dataIndex: 'reason', ellipsis: true },
            {
              title: '时间', dataIndex: 'createdAt', width: 140,
              render: (v: string) => fmtTime(v),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default PointsAccounts;
